import React, { useEffect, useState } from "react";
import { api, formatEUR, formatDate, isNetworkError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Printer } from "lucide-react";
import { sendFiscalReport, fiscalErrorMessage } from "@/lib/fiscal";
import { enqueueClosure, newOfflineId, readClosureQueue } from "@/lib/offlineQueue";
import { flushOfflineSales, SERVER_ATTEMPT } from "@/lib/offlineSales";
import { getPrinters } from "@/lib/usePrecontoPrinter";
import { sendRawTcp } from "@/lib/printTransport";
import { buildClosureEscpos } from "@/lib/escpos";
import { Pagination } from "@/components/Pagination";
import { getPageSize, setPageSize } from "@/lib/listDensity";

const paymentLabel = (k) => ({ cash: "Contanti", card: "Carta", ticket: "Ticket", mixed: "Misto" }[k] || k || "Altro");

export default function Closures() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setSize] = useState(getPageSize);
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 1 });

  const load = (p = page) =>
    api.get(`/closures?page=${p}&page_size=${pageSize}`).then((r) => {
      setItems(Array.isArray(r.data.items) ? r.data.items : []);
      setPageInfo({ total: r.data.total || 0, pages: r.data.pages || 1 });
    });
  useEffect(() => { load(page); }, [page, pageSize]);

  // Copia non fiscale del rapporto su stampante preconto (o comanda) via ESC/POS
  const printCopy = async (kind, closure) => {
    try {
      const printers = await getPrinters();
      const usable = (p) => p.ip && p.model !== "simulator";
      const target = printers.find((p) => p.role === "preconto" && usable(p)) || printers.find((p) => p.role === "comanda" && usable(p));
      if (!target) return;
      const tenant = (await api.get("/tenant/me")).data;
      const r = await sendRawTcp({
        host: target.ip,
        port: target.port || 9100,
        data: buildClosureEscpos({ tenant, closure }),
        encoding: "ISO-8859-1",
      });
      if (!r.ok) throw new Error(r.detail || r.reason || "stampa fallita");
      toast.success(`Copia stampata su "${target.name}"`);
    } catch (e) {
      toast.error(`Copia su "${e?.printerName || "preconto/comande"}" non stampata: ${String(e?.message || e)}`, { duration: 8000 });
    }
  };

  const make = async (kind) => {
    setBusy(true);
    try {
      const label = kind === "z" ? "Chiusura Z" : "Lettura X";
      if (kind === "z" && readClosureQueue().some((c) => c.kind === "z")) {
        await flushOfflineSales();
        toast.success("Chiusura Z già uscita sulla fiscale. Si registra sul server quando torna la linea.");
        return;
      }
      const res = await sendFiscalReport(kind);
      if (!res.ok && res.reason !== "no_fiscal_printer") {
        toast.error(fiscalErrorMessage(res), { duration: 8000 });
        return;
      }
      const offlineId = newOfflineId();
      const printedAt = new Date().toISOString();
      try {
        const r = await api.post(
          `/closures?kind=${kind}&offline_id=${encodeURIComponent(offlineId)}&at=${encodeURIComponent(printedAt)}`,
          null,
          SERVER_ATTEMPT,
        );
        toast.success(kind === "x" ? "Lettura X eseguita" : "Chiusura Z eseguita");
        if (res.ok) {
          toast.success(`${label} sulla fiscale "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
        }
        if (page === 1) load(1); else setPage(1);
        await printCopy(kind, r.data);
      } catch (e) {
        if (!(isNetworkError(e) || e?.isNetworkError)) {
          toast.error(e?.response?.data?.detail || "Errore");
          return;
        }
        if (!res.ok) {
          toast.error("Server non raggiungibile e nessuna stampante fiscale.");
          return;
        }
        enqueueClosure({ offline_id: offlineId, kind, created_at: printedAt, printed: true });
        toast.success(`${label} uscita sulla fiscale. Si registra sul server quando torna la linea.`);
        flushOfflineSales();
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Fiscale</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Chiusure & Letture</h1>
      <p className="text-velta-mute mb-8">
        Letture X (non azzerano) e chiusure Z (fine giornata). Il rapporto esce prima sulla stampante fiscale;
        il server lo registra dopo. Senza linea resta in attesa e non viene ristampato.
      </p>

      <div className="flex gap-3 mb-8">
        <button data-testid="closure-x-btn" disabled={busy} onClick={() => make("x")} className="h-12 px-6 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md font-medium flex items-center gap-2 btn-tactile disabled:opacity-40">
          <FileText className="w-4 h-4" /> Lettura X
        </button>
        <button data-testid="closure-z-btn" disabled={busy} onClick={() => make("z")} className="h-12 px-6 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile disabled:opacity-40">
          <FileText className="w-4 h-4" /> Chiusura Z
        </button>
        {busy && <span className="self-center text-sm text-velta-mute font-mono flex items-center gap-1.5"><Printer className="w-4 h-4 animate-pulse" /> Invio alla stampante…</span>}
      </div>

      <div className="space-y-3">
        {(Array.isArray(items) ? items : []).map((c) => (
          <div key={c.id} className="bg-white border border-velta-border rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className={`inline-block px-2 py-1 text-xs font-mono uppercase tracking-widest rounded ${c.kind === "z" ? "bg-velta-ink text-white" : "bg-velta-bg text-velta-ink border border-velta-border"}`}>
                  {c.kind === "z" ? "Chiusura Z" : "Lettura X"}
                </span>
                <span className="ml-3 font-mono text-sm text-velta-mute">{formatDate(c.created_at)}</span>
              </div>
              <div className="font-mono text-2xl font-bold">{formatEUR(c.total)}</div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-velta-mute">
              <div><span className="font-mono uppercase tracking-widest text-xs">Scontrini:</span> <span className="font-mono font-medium text-velta-ink">{c.count}</span></div>
              {Object.entries(c.by_payment || {}).map(([k, v]) => (
                <div key={k}><span className="font-mono uppercase tracking-widest text-xs">{paymentLabel(k)}:</span> <span className="font-mono font-medium text-velta-ink">{formatEUR(v)}</span></div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white border border-velta-border rounded-md p-12 text-center text-velta-mute">Nessuna chiusura ancora.</div>}
        <Pagination
          page={page}
          pages={pageInfo.pages}
          total={pageInfo.total}
          onPage={setPage}
          pageSize={pageSize}
          onPageSize={(n) => { setSize(setPageSize(n)); setPage(1); }}
        />
      </div>
    </div>
  );
}
